import React, { useMemo, useRef } from "react";
import {
  Box,
  Button,
  EntitySelectorTrigger,
  Icon,
  Text,
  selectTriggerIconClassName,
  selectTriggerVariants,
  useEntitySelector,
  type EntitySelection,
  type IconName,
  cn,
} from "@salestrak/ui";

import {
  EntityGroupTag,
  EntitySelectionHover,
  EntityTag,
} from "#app/components/entity";
import { InlineTriggerAction } from "#app/components/entity/row-actions";
import { NONE_OPTION_LABEL } from "#app/lib/custom-field-utils";
import { type EntityData } from "#app/lib/entity-visuals";
import { type SelectorSize } from "./sizes";
import type { Omittable } from "@salestrak/ui";

// The hover wraps the value INSIDE the button (below the popover trigger) so
// the click still reaches the picker and the hover popover never steals it.

// Hover-revealed trailing actions are pure CSS, not a touch-device hook (the
// address row's grammar — always visible on coarse pointers).
const ACTION_REVEAL =
  "opacity-0 transition-opacity group-hover/seltrigger:opacity-100 focus-within:opacity-100 pointer-coarse:opacity-100";

type ButtonVariant = React.ComponentProps<typeof Button>["variant"];
type ButtonSize = React.ComponentProps<typeof Button>["size"];
type ButtonShape = React.ComponentProps<typeof Button>["shape"];

function toButtonSize(size: SelectorSize | undefined): ButtonSize {
  switch (size) {
    case "xs":
      return "xs";
    case "sm":
      return "sm";
    case "lg":
    case "xl":
      return "lg";
    default:
      return "default";
  }
}

export type SelectionEntityTriggerProps = Omittable<{
  toEntity: (sel: EntitySelection) => EntityData;
  placeholder: string;
  /** The trigger's accessible name, when a visible label names the field
   *  ("Add to team"). Without it the button is named by what it shows. */
  "aria-label"?: string;
  title?: string;
  emptyIcon?: IconName;
  /** Text tone for an empty trigger. */
  emptyTextVariant?: React.ComponentProps<typeof Text>["variant"];
  buttonVariant?: ButtonVariant;
  size?: SelectorSize;
  /** Trigger corners. "square" when the trigger is row chrome (a property
   *  row's value) rather than a free-standing control. */
  shape?: ButtonShape;
  disabled?: boolean;
  // Read-only: render the current selection as a static, full-color value
  // (no picker, no press, no hover/cursor) instead of a dimmed disabled
  // button. Use for view/comment-tier viewers who can see but not edit.
  readOnly?: boolean;
  fill?: boolean;
  showChevron?: boolean;
  /** De-emphasize the selected value's label (muted text). */
  valueMuted?: boolean;
  /** Tight packing for a stand-alone hero control (a type label button): a
   *  4px glyph↔label gap and a 4px button gap in place of the 8px/6px a row
   *  value keeps, so glyph, name and caret read as one word. */
  dense?: boolean;
  /** Quiet lead-in before a single selected value ("Reports to" → "Reports to
   *  Blake Harmon"). Names the relationship when the row's label is hidden and
   *  the leading glyph alone would leave the value ambiguous. Not shown for
   *  the avatar-only multi-selection stack. */
  valuePrefix?: string;
  /** How a single selected value renders. `"label"` drops the avatar and shows
   *  the name alone — the hover preview still carries the identity. A
   *  multi-selection keeps the avatar stack regardless. */
  valueDisplay?: "full" | "label";
  /** Wear the Select field's exact anatomy — hairline, fill, value on the
   *  left, stacked chevrons on the right — for a picker sitting among selects
   *  (a settings row). The button props above (`buttonVariant`, `shape`,
   *  `size`, `showChevron`, `dense`) do not apply. Empty reads as "—": the
   *  row's own label already names the field. The stack drops to the 20px
   *  rung so it sits on the field's text line like the single value's tile —
   *  24px tiles against the field's own edge read as the control, not its
   *  value. */
  field?: boolean;
  /** What the empty `field` reads. Default is the em dash, for a row whose
   *  own label names the field; two pickers under ONE label (Workflow then
   *  Stage) name themselves here instead. */
  fieldEmptyLabel?: string;
  /** Tooltip + accessible label of the open action. Default "Open" — pass the
   *  verb the destination earns ("View profile"). */
  openLabel?: string;
  /** Tooltip + accessible label of the clear action. Default "Clear". */
  clearLabel?: string;
  /** Hover-revealed open action (arrow-up-right) on the value area, before the
   *  clear × — shown only while something is selected (always visible on
   *  touch). Kept in read-only mode: opening the linked entity is not an
   *  edit. */
  onOpen?: () => void;
  /** Hover-revealed clear-all × on the far right of the value area (always
   *  visible on touch) — shown only while something is selected. */
  onClear?: () => void;
}>;

export function SelectionEntityTrigger({
  toEntity,
  placeholder,
  "aria-label": ariaLabel,
  title,
  emptyIcon,
  emptyTextVariant = "muted",
  buttonVariant = "ghost",
  size = "sm",
  shape,
  disabled,
  readOnly = false,
  fill,
  showChevron = false,
  valueMuted,
  dense = false,
  valuePrefix,
  valueDisplay,
  field = false,
  fieldEmptyLabel,
  onOpen,
  onClear,
  openLabel = "Open",
  clearLabel = "Clear",
}: SelectionEntityTriggerProps) {
  const { selections, open } = useEntitySelector();

  // Freeze the displayed selection while the picker is open so the trigger
  // doesn't resize and shift the popover anchor.
  const snapshotRef = useRef(selections);
  if (!open) snapshotRef.current = selections;
  const displayed = snapshotRef.current;

  const entities = useMemo(() => displayed.map(toEntity), [displayed, toEntity]);

  const primary = entities[0];
  const extra = entities.length - 1;

  const openAction =
    onOpen && displayed.length > 0 ? (
      <InlineTriggerAction
        className={ACTION_REVEAL}
        icon="arrow-up-right"
        label={openLabel}
        onClick={onOpen}
      />
    ) : null;

  // Read-only: a plain row with no EntitySelectorTrigger/Button — so there's
  // no press to open the picker and no cursor/hover-bg/dim of an editable
  // control. The hover preview (EntitySelectionHover) is kept, so viewers can
  // still hover the value to peek at the entities. Full-color value so it
  // reads as information; padding matches the ghost trigger's text inset so it
  // lines up with the editable state.
  if (readOnly) {
    return (
      <Box
        direction="row"
        align="center"
        gap="xs"
        // The static value aligns with the editable trigger's text: the
        // Button rung's own label inset (default 12px, the compact rungs 8px).
        paddingX={size === "xs" || size === "sm" ? "sm" : "base"}
        height={32}
        minWidth={0}
        shrink
        className="group/seltrigger"
      >
        {primary ? (
          extra > 0 ? (
            // The value rule: a single selection reads as avatar + name; 2+
            // collapse to the avatar-only stack ("sm" = 24px in the 32px
            // drawer trigger).
            <EntityGroupTag
              entities={entities}
              variant="stack"
              size="sm"
              title={title}
              interactive={false}
            />
          ) : (
            // No picker here to claim the press, so a tap may open the preview.
            <EntitySelectionHover entity={primary} clickOpensPreview>
              <Box direction="row" align="center" gap="xs" shrink>
                {valuePrefix ? (
                  <Box shrink={false}>
                    <Text size="sm" variant="muted">
                      {valuePrefix}
                    </Text>
                  </Box>
                ) : null}
                <EntityTag
                  entity={primary}
                  display={valueDisplay}
                  weight="regular"
                  labelInk={valueMuted ? "muted" : undefined}
                  visualGap={dense ? "xs" : undefined}
                  interactive={false}
                />
              </Box>
            </EntitySelectionHover>
          )
        ) : emptyIcon ? (
          <>
            <Box width={20} height={20} align="center" justify="center">
              <Icon name={emptyIcon} size="md" color="muted" />
            </Box>
            <Text size="sm" variant={emptyTextVariant} truncate={1}>
              {placeholder}
            </Text>
          </>
        ) : (
          <Text size="sm" variant={emptyTextVariant} truncate={1}>
            {placeholder}
          </Text>
        )}
        {openAction}
      </Box>
    );
  }

  // Field: the SelectTrigger's dress on the popover trigger — the same
  // composition as the follow-ups' vocabulary field trigger, holding an
  // entity value. The trigger's own props (the click, `aria-expanded`, the
  // anchor ref) land on this button through `render`.
  if (field) {
    return (
      <EntitySelectorTrigger
        disabled={disabled}
        aria-label={ariaLabel}
        render={
          <Box
            as="button"
            type="button"
            className={cn(
              // `fill` spans the field column (a form row, a list column);
              // otherwise the trigger hugs its value.
              selectTriggerVariants({ variant: "default", width: fill ? "full" : "auto" }),
              // The column that asked for `fill` owns the width, floor included.
              fill && "min-w-0",
              "group/seltrigger",
              disabled && "pointer-events-none opacity-64",
            )}
          >
            {primary ? (
              extra > 0 ? (
                <EntityGroupTag
                  entities={entities}
                  variant="stack"
                  size="xs"
                  title={title}
                  interactive={false}
                  hoverDisabled={open}
                />
              ) : (
                <EntitySelectionHover entity={primary} disabled={open}>
                  <Box direction="row" align="center" gap="xs" shrink>
                    <EntityTag
                      entity={primary}
                      display={valueDisplay}
                      weight="regular"
                      labelInk={valueMuted ? "muted" : undefined}
                      interactive={false}
                    />
                  </Box>
                </EntitySelectionHover>
              )
            ) : (
              <Text size="sm" variant="muted" truncate={1}>
                {fieldEmptyLabel ?? NONE_OPTION_LABEL}
              </Text>
            )}
            <Icon className={selectTriggerIconClassName} name="chevrons-up-down" />
          </Box>
        }
      />
    );
  }

  return (
    <EntitySelectorTrigger
      disabled={disabled}
      aria-label={ariaLabel}
      render={
        <Button
          variant={buttonVariant}
          size={toButtonSize(size)}
          shape={shape}
          disabled={disabled}
          justify="start"
          fullWidth={fill}
          // The value is content, so the button yields width instead of holding
          // its natural size: a long selection truncates inside the trigger
          // rather than pushing the chevron and the hover open/clear actions
          // past the edge of the row that hosts it.
          // `dense` narrows the button's own gap; a library gap signal —
          // Button has no packing axis yet. (className = tracked library gap.)
          className={cn(
            "group/seltrigger min-w-0 shrink font-normal",
            dense && "[--button-gap:--spacing(1)]",
          )}
        >
          {primary ? (
            extra > 0 ? (
              // Multi-selection: the avatar-only stack ("sm" = 24px in the
              // 32px trigger), never a hand-rolled "+N" text. The trigger
              // button owns the press.
              <EntityGroupTag
                entities={entities}
                variant="stack"
                size="sm"
                title={title}
                interactive={false}
                hoverDisabled={open}
              />
            ) : (
              <EntitySelectionHover entity={primary} disabled={open}>
                <Box direction="row" align="center" gap="xs" shrink>
                  {valuePrefix ? (
                    <Box shrink={false}>
                      <Text size="sm" variant="muted">
                        {valuePrefix}
                      </Text>
                    </Box>
                  ) : null}
                  <EntityTag
                    entity={primary}
                    display={valueDisplay}
                    weight="regular"
                    labelInk={valueMuted ? "muted" : undefined}
                    visualGap={dense ? "xs" : undefined}
                    interactive={false}
                  />
                </Box>
              </EntitySelectionHover>
            )
          ) : emptyIcon ? (
            <>
              <Box width={20} height={20} align="center" justify="center">
                <Icon name={emptyIcon} size="md" color="muted" />
              </Box>
              <Text size="sm" variant={emptyTextVariant} truncate={1}>
                {placeholder}
              </Text>
            </>
          ) : (
            <Text size="sm" variant={emptyTextVariant} truncate={1}>
              {placeholder}
            </Text>
          )}
          {showChevron ? (
            // The 24px property line carries 16px glyphs, matching the row's
            // leading icon; the taller rungs keep the 14px caret.
            <Icon
              name="chevron-down"
              size={size === "xs" ? "sm" : "xs"}
              color="muted"
            />
          ) : null}
          {(onOpen || onClear) && displayed.length > 0 ? (
            <>
              <Box grow />
              {openAction}
              {onClear ? (
                <InlineTriggerAction
                  className={ACTION_REVEAL}
                  icon="x"
                  label={clearLabel}
                  onClick={onClear}
                />
              ) : null}
            </>
          ) : null}
        </Button>
      }
    />
  );
}
